import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { toast } from 'react-toastify';
import { BrowserTitle } from '../components';
import api from '../api/axios';

const ImportCard = ({ title, description, icon, fields, onUpload, loading, result }) => {
  const [file, setFile] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    await onUpload(file);
    setFile(null);
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Expected CSV columns:</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {fields}
        </Typography>
        
        {result && (
          <Alert 
            severity={result.success ? 'success' : 'error'} 
            sx={{ mt: 2 }}
            icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
          >
            {result.message}
            {result.details && (
              <List dense sx={{ mt: 1, py: 0 }}>
                {Object.entries(result.details).map(([key, value]) => (
                  <ListItem key={key} sx={{ py: 0 }}>
                    <ListItemText 
                      primary={`${key}: ${value}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Box sx={{ width: '100%' }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              component="label"
              disabled={loading}
              sx={{ flexGrow: 1 }}
            >
              {file ? file.name : 'Select CSV'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={!file || loading}
            >
              Upload
            </Button>
          </Box>
        </Box>
      </CardActions>
    </Card>
  );
};

const DataImport = () => {
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [resultClients, setResultClients] = useState(null);
  const [resultProposals, setResultProposals] = useState(null);

  const handleClientsUpload = async (file) => {
    setLoadingClients(true);
    setResultClients(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/import/clients-properties', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultClients({
        success: true,
        message: 'Import completed successfully!',
        details: response.data,
      });
      toast.success('Clients & Properties imported successfully');
    } catch (error) {
      setResultClients({
        success: false,
        message: error.response?.data?.message || 'Import failed',
      });
      toast.error('Import failed');
    } finally {
      setLoadingClients(false);
    }
  };

  const handleProposalsUpload = async (file) => {
    setLoadingProposals(true);
    setResultProposals(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/import/legacy-proposals', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultProposals({
        success: true,
        message: 'Import completed successfully!',
        details: response.data,
      });
      toast.success('Legacy Proposals imported successfully');
    } catch (error) {
      setResultProposals({
        success: false,
        message: error.response?.data?.message || 'Import failed',
      });
      toast.error('Import failed');
    } finally {
      setLoadingProposals(false);
    }
  };

  return (
    <Box component="section">
      <BrowserTitle title="Data Import | Airmaxx Pro" />
      
      <Typography variant="h5" sx={{ mb: 1 }}>Data Import</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Import data from legacy system CSV files. Only superadmins can access this page.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Important:</strong> Make sure CSV files are properly formatted. 
        Duplicate records will be skipped automatically.
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <ImportCard
          title="Clients & Properties"
          description="Import clients with their associated properties. Creates new clients and properties, links them together."
          icon={<PersonIcon color="primary" />}
          fields="client_id, firstname, lastname, email, phone, mobile, property_id, streetNumber, streetName, unit, city, zipcode"
          onUpload={handleClientsUpload}
          loading={loadingClients}
          result={resultClients}
        />

        <ImportCard
          title="Legacy Proposals"
          description="Import legacy proposals and link them to existing properties by sourceId."
          icon={<DescriptionIcon color="secondary" />}
          fields="property_id, proposal_id, proposal_status, proposal_date, public_url"
          onUpload={handleProposalsUpload}
          loading={loadingProposals}
          result={resultProposals}
        />
      </Box>
    </Box>
  );
};

export default DataImport;
